import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { capitalize } from 'lodash'

import IsolatedState from 'components/common/IsolatedState'
import Text from 'components/common/Text'
import Time from 'components/attacks/details/Time'
import Dot from 'components/attacks/details/Dot'
import Icon from 'components/Icon'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import Flex from 'components/common/Flex'
import theme from 'theme'

export function MetricsLinks({ metricsLinks = {}, attackId }) {
  if (!Object.keys(metricsLinks).length) return null

  return (
    <Fragment>
      {Object.entries(metricsLinks).map(([provider, link]) => {
        return (
          <div key={provider}>
            <TrackedExternalLink
              css={{ display: 'inline' }}
              eventName="ExecutionMetricsClick"
              eventProperties={{
                provider,
                attackId
              }}
              href={link}
            >
              <Text underline as="small" color="normal" weight="normal">
                {capitalize(provider)} Metrics
              </Text>
            </TrackedExternalLink>
          </div>
        )
      })}
    </Fragment>
  )
}

MetricsLinks.propTypes = {
  attackId: PropTypes.string,
  metricsLinks: PropTypes.object
}

function ExpandedOutput({ title, text }) {
  return (
    <div css={{ padding: theme.space[3], minHeight: 48 }}>
      <h3>{title}</h3>
      <code
        css={{
          display: 'block',
          border: theme.border.base,
          padding: theme.space[3],
          marginTop: theme.space[3],
          whiteSpace: 'pre-line',
          lineHeight: '2'
        }}
      >
        {text}
      </code>
    </div>
  )
}

ExpandedOutput.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string
}

export default function Execution({ attackId, execution, isInitiallyOpen }) {
  const {
    guest_id: guestId,
    client_id: clientId,
    client_version: clientVersion,
    stage,
    start_time: startTime,
    end_time: endTime,
    metrics_links: metricsLinks,
    output = '',
    error = ''
  } = execution
  const sanitizedOutput = output.replace('\n', '\u000A')
  const sanitizedError = error.replace('\n', '\u000A')
  const isExpandable = Boolean(sanitizedOutput) || Boolean(sanitizedError)

  return (
    <IsolatedState initialValue={Boolean(isInitiallyOpen)}>
      {({ value: isOpen, setValue: setOpenValue }) => (
        <section
          data-test-execution
          css={{
            flex: 1,
            fontSize: theme.fontSizes[2],
            marginTop: theme.space[2]
          }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns:
                '32px minmax(96px,1fr) minmax(96px,0.3fr) 48px',
              gap: theme.space[2],
              alignItems: 'center',
              padding: theme.space[2],
              paddingLeft: 0,
              transition: '150ms background-color',
              cursor: isExpandable ? 'pointer' : 'default',

              '&:hover': isExpandable && {
                backgroundColor: theme.colors.grayOverlay[0]
              }
            }}
            onClick={() => isExpandable && setOpenValue(!isOpen)}
          >
            <Dot css={{ justifySelf: 'center' }} stage={stage} />
            <div>
              <label
                css={{
                  flex: 1,
                  cursor: isExpandable ? 'inherit' : 'default'
                }}
              >
                <Flex>
                  <Text
                    as="p"
                    color="dark"
                    css={{
                      marginRight: theme.space[2]
                    }}
                  >
                    {clientId}
                  </Text>
                  <Text as="small" color="normal" weight="normal">
                    Gremlin v{clientVersion}
                  </Text>
                </Flex>
                {guestId && (
                  <Text
                    as="small"
                    css={{
                      wordBreak: 'break-all',
                      marginTop: theme.space[2]
                    }}
                  >
                    Container ID: {guestId}
                  </Text>
                )}
              </label>
              <MetricsLinks attackId={attackId} metricsLinks={metricsLinks} />
            </div>

            <div
              css={{
                display: 'grid',
                alignItems: 'center',
                justifyItems: 'end',
                columnGap: theme.space[2],
                gridTemplateColumns: '0.5fr 0.5fr',
                justifySelf: 'end',
                whiteSpace: 'nowrap'
              }}
            >
              {startTime && <Time label="Start" time={startTime} />}
              {endTime && <Time label="End" time={endTime} />}
            </div>
            {isExpandable && (
              <div
                css={{
                  fontSize: theme.fontSizes[4],
                  marginLeft: theme.space[3]
                }}
              >
                {isOpen ? (
                  <Icon icon="chevronDown" />
                ) : (
                  <Icon icon="chevronForward" />
                )}
              </div>
            )}
          </div>
          {isOpen && (
            <Fragment>
              {sanitizedOutput && (
                <ExpandedOutput text={sanitizedOutput} title="Output" />
              )}
              {sanitizedError && (
                <ExpandedOutput text={sanitizedError} title="Error" />
              )}
            </Fragment>
          )}
        </section>
      )}
    </IsolatedState>
  )
}

Execution.propTypes = {
  attackId: PropTypes.string,
  execution: PropTypes.object,
  isInitiallyOpen: PropTypes.bool
}
